import React from 'react';
import { Dialog } from 'primereact/dialog';
import { StudyMaterial, StudyMaterialVideo } from '../StudyMaterials';

/* ─── Design tokens (matches StudyMaterials.tsx) ─────────────────────── */
const C = {
  navy:       '#0e225d',
  navyMid:    '#1a3370',
  navyDark:   '#0a1a3d',
  gold:       '#fbb21f',
  goldDim:    'rgba(251,178,31,0.16)',
  goldBorder: 'rgba(251,178,31,0.35)',
  white:      '#ffffff',
  border:     '#e5e7eb',
  bg:         '#f5f7fa',
  muted:      '#9ca3af',
  sub:        '#6b7280',
};

/* ─── Helpers ──────────────────────────────────────────---───────────────── */
const formatDate = (s: string) => {
  try {
    return new Date(s).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return s; }
};

const formatFileSize = (bytes: number): string => {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return (bytes / Math.pow(k, i)).toFixed(1) + ' ' + sizes[i];
};

const getFileIcon = (mimeType: string): string => {
  if (mimeType?.includes('pdf'))                               return 'pi pi-file-pdf';
  if (mimeType?.includes('word'))                              return 'pi pi-file-word';
  if (mimeType?.includes('excel') || mimeType?.includes('spreadsheet')) return 'pi pi-file-excel';
  if (mimeType?.includes('image'))                             return 'pi pi-image';
  return 'pi pi-file';
};

const getFileIconColor = (mimeType: string): string => {
  if (mimeType?.includes('pdf'))   return '#dc2626';
  if (mimeType?.includes('word'))  return '#2563eb';
  if (mimeType?.includes('excel') || mimeType?.includes('spreadsheet')) return '#16a34a';
  if (mimeType?.includes('image')) return '#7c3aed';
  return C.navy;
};

/* ─── Section wrapper ────────────────────────────────────────────────── */
const Section = ({ icon, title, count, children }: { icon: string; title: string; count: number; children: React.ReactNode }) => (
  <div style={{ marginBottom: '14px' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
      <div style={{ width: '3px', height: '14px', background: `linear-gradient(180deg, ${C.gold} 0%, #e09a00 100%)`, borderRadius: '3px' }} />
      <span style={{ fontSize: '0.78rem', fontWeight: '700', color: C.navy, display: 'flex', alignItems: 'center', gap: '6px' }}>
        <i className={icon} style={{ fontSize: '0.72rem' }} />
        {title}
        <span style={{ padding: '1px 7px', borderRadius: '10px', background: C.goldDim, border: `1px solid ${C.goldBorder}`, color: '#92580a', fontSize: '0.68rem', fontWeight: '700' }}>
          {count}
        </span>
      </span>
    </div>
    {children}
  </div>
);

/* ─── Props ──────────────────────────────────────────────────────────── */
interface ViewStudyMaterialModalProps {
  visible: boolean;
  material: StudyMaterial | null;
  onHide: () => void;
}

const ViewStudyMaterialModal: React.FC<ViewStudyMaterialModalProps> = ({ visible, material, onHide }) => {
  if (!material) return null;

  const videos     = material.videos      || [];
  const files      = material.files       || [];
  const videoLinks = material.video_links || [];

  const hasBody        = material.body && material.body.trim() !== '' && material.body.trim() !== '.';
  const hasVideos      = videos.length > 0;
  const hasFiles       = files.length > 0;
  const hasVideoLinks  = videoLinks.length > 0;

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      style={{ width: '92vw', maxWidth: '780px' }}
      dismissableMask
      draggable={false}
      header={false}
      pt={{
        root:    { style: { borderRadius: '18px', overflow: 'hidden', boxShadow: '0 20px 60px rgba(14,34,93,0.22)', border: `1px solid ${C.border}` } },
        header:  { style: { display: 'none' } },
        content: { style: { padding: 0 } },
      }}
    >
      <div>
        {/* ── Modal Header ──────────────────────────────────────────────── */}
        <div style={{
          background: `linear-gradient(180deg, ${C.navy} 0%, ${C.navyMid} 55%, ${C.navyDark} 100%)`,
          padding: '22px 24px',
          position: 'relative', overflow: 'hidden',
        }}>
          {/* Decorative blobs */}
          <div style={{ position: 'absolute', top: '-40px', right: '-40px', width: '160px', height: '160px', background: C.goldDim, borderRadius: '50%', filter: 'blur(35px)', pointerEvents: 'none' }} />
          <div style={{ position: 'absolute', bottom: '-20px', left: '20%', width: '100px', height: '100px', background: 'rgba(251,178,31,0.05)', borderRadius: '50%', filter: 'blur(25px)', pointerEvents: 'none' }} />

          <div style={{ position: 'relative', zIndex: 1 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                {/* Badge row */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '10px' }}>
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '3px 10px', background: C.goldDim, border: `1px solid ${C.goldBorder}`, borderRadius: '20px', fontSize: '0.65rem', fontWeight: '700', letterSpacing: '0.6px', textTransform: 'uppercase', color: C.gold }}>
                    <i className="pi pi-book" style={{ fontSize: '0.62rem' }} />
                    Study Material
                  </div>
                  {material.is_course_module_active && (
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '3px 10px', background: 'rgba(22,163,74,0.18)', border: '1px solid rgba(22,163,74,0.35)', borderRadius: '20px', fontSize: '0.65rem', fontWeight: '700', letterSpacing: '0.6px', textTransform: 'uppercase', color: '#86efac' }}>
                      <i className="pi pi-check-circle" style={{ fontSize: '0.62rem' }} />
                      Course Module
                    </div>
                  )}
                </div>

                <h2 style={{ margin: '0 0 8px 0', fontSize: '1.1rem', fontWeight: '700', color: C.white, lineHeight: '1.35', letterSpacing: '-0.3px' }}>
                  {material.title}
                </h2>

                {/* Stats row */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.7rem', color: 'rgba(255,255,255,0.55)', fontWeight: '500' }}>
                    <i className="pi pi-calendar" style={{ fontSize: '0.65rem', color: C.gold }} />
                    {formatDate(material.created_at)}
                  </span>
                  {hasVideos && (
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.7rem', color: 'rgba(255,255,255,0.7)', fontWeight: '600' }}>
                      <i className="pi pi-video" style={{ fontSize: '0.65rem', color: C.gold }} />
                      {videos.length} video{videos.length !== 1 ? 's' : ''}
                    </span>
                  )}
                  {hasFiles && (
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.7rem', color: 'rgba(255,255,255,0.7)', fontWeight: '600' }}>
                      <i className="pi pi-paperclip" style={{ fontSize: '0.65rem', color: C.gold }} />
                      {files.length} file{files.length !== 1 ? 's' : ''}
                    </span>
                  )}
                  {hasVideoLinks && (
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.7rem', color: 'rgba(255,255,255,0.7)', fontWeight: '600' }}>
                      <i className="pi pi-link" style={{ fontSize: '0.65rem', color: C.gold }} />
                      {videoLinks.length} link{videoLinks.length !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>
              </div>

              {/* Close button */}
              <button
                onClick={onHide}
                style={{ background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '8px', width: '30px', height: '30px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.65)', transition: 'all 0.15s ease', flexShrink: 0 }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.2)'; e.currentTarget.style.color = C.white; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; e.currentTarget.style.color = 'rgba(255,255,255,0.65)'; }}
              >
                <i className="pi pi-times" style={{ fontSize: '0.75rem' }} />
              </button>
            </div>
          </div>
        </div>

        {/* ── Body ──────────────────────────────────────────────────────── */}
        <div style={{ padding: '20px 24px', maxHeight: '60vh', overflowY: 'auto' }}>

          {/* Description / Notes */}
          {hasBody && (
            <div style={{ marginBottom: '16px', padding: '14px 16px', background: C.bg, borderRadius: '10px', border: `1px solid ${C.border}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
                <div style={{ width: '3px', height: '14px', background: `linear-gradient(180deg, ${C.gold} 0%, #e09a00 100%)`, borderRadius: '3px' }} />
                <span style={{ fontSize: '0.75rem', fontWeight: '700', color: C.navy }}>
                  <i className="pi pi-align-left" style={{ fontSize: '0.7rem', marginRight: '5px' }} />
                  Notes
                </span>
              </div>
              <p style={{ margin: 0, fontSize: '0.82rem', color: C.sub, lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>
                {material.body}
              </p>
            </div>
          )}

          {/* ── HLS Videos ─────────────────────────────────────────────── */}
          {hasVideos && (
            <Section icon="pi pi-video" title="Videos" count={videos.length}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {videos.map((video: StudyMaterialVideo, index: number) => (
                  <div
                    key={video.video_id || index}
                    style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '11px 14px', background: C.white, border: `1px solid ${C.border}`, borderRadius: '9px', transition: 'all 0.15s ease' }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = C.goldBorder; e.currentTarget.style.background = 'rgba(251,178,31,0.03)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.background = C.white; }}
                  >
                    {/* Icon */}
                    <div style={{ width: '36px', height: '36px', borderRadius: '9px', background: `linear-gradient(135deg, ${C.navy} 0%, ${C.navyMid} 100%)`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <i className="pi pi-play-circle" style={{ fontSize: '0.95rem', color: C.gold }} />
                    </div>

                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '0.8rem', fontWeight: '600', color: C.navy, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginBottom: '2px' }}>
                        {video.original_name || `Video ${index + 1}`}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {video.size > 0 && (
                          <span style={{ fontSize: '0.68rem', color: C.muted }}>{formatFileSize(video.size)}</span>
                        )}
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', padding: '1px 7px', borderRadius: '10px', background: video.status === 'ready' ? 'rgba(22,163,74,0.1)' : 'rgba(251,178,31,0.12)', border: `1px solid ${video.status === 'ready' ? 'rgba(22,163,74,0.25)' : C.goldBorder}`, fontSize: '0.65rem', fontWeight: '700', color: video.status === 'ready' ? '#15803d' : '#92580a' }}>
                          <i className={`pi ${video.status === 'ready' ? 'pi-check-circle' : 'pi-clock'}`} style={{ fontSize: '0.58rem' }} />
                          {video.status === 'ready' ? 'Ready' : video.status}
                        </span>
                      </div>
                    </div>

                    {video.m3u8_url && video.status === 'ready' && (
                      <a
                        href={video.m3u8_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        style={{ padding: '6px 12px', background: `linear-gradient(135deg, ${C.navy} 0%, ${C.navyMid} 100%)`, color: C.white, textDecoration: 'none', borderRadius: '7px', fontSize: '0.72rem', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0, transition: 'all 0.15s ease' }}
                        onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.03)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
                      >
                        <i className="pi pi-play" style={{ fontSize: '0.65rem' }} />
                        Play
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* ── Files ──────────────────────────────────────────────────── */}
          {hasFiles && (
            <Section icon="pi pi-paperclip" title="Attachments" count={files.length}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {files.map((file, index) => (
                  <div
                    key={index}
                    style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '11px 14px', background: C.white, border: `1px solid ${C.border}`, borderRadius: '9px', transition: 'all 0.15s ease' }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = C.goldBorder; e.currentTarget.style.background = 'rgba(251,178,31,0.03)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.background = C.white; }}
                  >
                    <div style={{ width: '36px', height: '36px', borderRadius: '9px', background: C.bg, border: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <i className={getFileIcon(file.mime_type)} style={{ fontSize: '1rem', color: getFileIconColor(file.mime_type) }} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '0.8rem', fontWeight: '600', color: C.navy, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginBottom: '2px' }}>
                        {file.file_name}
                      </div>
                      <div style={{ fontSize: '0.68rem', color: C.muted }}>{formatFileSize(file.size)}</div>
                    </div>
                    <a
                      href={file.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      style={{ padding: '6px 12px', background: `linear-gradient(135deg, ${C.navy} 0%, ${C.navyMid} 100%)`, color: C.white, textDecoration: 'none', borderRadius: '7px', fontSize: '0.72rem', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0, transition: 'all 0.15s ease' }}
                      onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.03)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
                    >
                      <i className="pi pi-download" style={{ fontSize: '0.65rem' }} />
                      Download
                    </a>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* ── External Video Links ────────────────────────────────────── */}
          {hasVideoLinks && (
            <Section icon="pi pi-link" title="Video Links" count={videoLinks.length}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {videoLinks.map((link, index) => (
                  <a
                    key={index}
                    href={link}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ padding: '11px 14px', background: C.white, border: `1px solid ${C.border}`, borderRadius: '9px', color: C.navy, textDecoration: 'none', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '10px', transition: 'all 0.15s ease' }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = C.goldBorder; e.currentTarget.style.background = 'rgba(251,178,31,0.03)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.background = C.white; }}
                  >
                    <div style={{ width: '28px', height: '28px', borderRadius: '7px', background: C.goldDim, border: `1px solid ${C.goldBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <i className="pi pi-external-link" style={{ fontSize: '0.72rem', color: '#92580a' }} />
                    </div>
                    <span style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '0.78rem', color: C.sub }}>{link}</span>
                  </a>
                ))}
              </div>
            </Section>
          )}

          {/* Empty state */}
          {!hasBody && !hasVideos && !hasFiles && !hasVideoLinks && (
            <div style={{ padding: '40px 20px', textAlign: 'center' }}>
              <div style={{ width: '44px', height: '44px', background: 'rgba(14,34,93,0.06)', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 10px' }}>
                <i className="pi pi-inbox" style={{ fontSize: '1.2rem', color: C.navy, opacity: 0.25 }} />
              </div>
              <p style={{ margin: 0, fontSize: '0.82rem', color: C.muted }}>No content available for this material</p>
            </div>
          )}
        </div>

        {/* ── Footer ────────────────────────────────────────────────────── */}
        <div style={{ padding: '14px 24px', borderTop: `1px solid ${C.border}`, display: 'flex', justifyContent: 'flex-end', background: C.bg }}>
          <button
            onClick={onHide}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '9px 22px', background: `linear-gradient(135deg, ${C.navy} 0%, ${C.navyMid} 100%)`, border: 'none', borderRadius: '9px', color: C.white, fontSize: '0.8rem', fontWeight: '700', cursor: 'pointer', boxShadow: '0 2px 8px rgba(14,34,93,0.22)', transition: 'all 0.15s ease' }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 4px 14px rgba(14,34,93,0.32)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(14,34,93,0.22)'; }}
          >
            <i className="pi pi-times" style={{ fontSize: '0.72rem' }} />
            Close
          </button>
        </div>
      </div>
    </Dialog>
  );
};

export default ViewStudyMaterialModal;

// import React from 'react';
// import { Dialog } from 'primereact/dialog';
// import { GlobalButton } from '../../../components/GlobalComponents/GlobalButton';

// interface StudyMaterialFile {
//   file_name: string;
//   key: string;
//   url: string;
//   size: number;
//   mime_type: string;
//   uploaded_at: string;
// }

// interface StudyMaterial {
//   id: string;
//   chapter_id?: string;
//   module_id?: string;
//   course_id: string;
//   institute_id?: string;
//   title: string;
//   body: string;
//   status: string;
//   is_course_module_active?: boolean;
//   files: StudyMaterialFile[];
//   video_links: string[];
//   created_at: string;
//   created_by?: string;
// }

// interface ViewStudyMaterialModalProps {
//   visible: boolean;
//   material: StudyMaterial | null;
//   onHide: () => void;
// }

// const ViewStudyMaterialModal: React.FC<ViewStudyMaterialModalProps> = ({
//   visible,
//   material,
//   onHide,
// }) => {
//   const formatDate = (dateString: string) => {
//     try {
//       const date = new Date(dateString);
//       return date.toLocaleDateString('en-IN', {
//         day: '2-digit',
//         month: 'short',
//         year: 'numeric',
//         hour: '2-digit',
//         minute: '2-digit'
//       });
//     } catch {
//       return dateString;
//     }
//   };

//   const formatFileSize = (bytes: number): string => {
//     if (bytes === 0) return '0 Bytes';
//     const k = 1024;
//     const sizes = ['Bytes', 'KB', 'MB'];
//     const i = Math.floor(Math.log(bytes) / Math.log(k));
//     return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
//   };

//   const getFileIcon = (mimeType: string): string => {
//     if (mimeType.includes('pdf')) return 'pi pi-file-pdf';
//     if (mimeType.includes('word')) return 'pi pi-file-word';
//     if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return 'pi pi-file-excel';
//     if (mimeType.includes('image')) return 'pi pi-image';
//     return 'pi pi-file';
//   };

//   if (!material) return null;

//   return (
//     <Dialog
//       visible={visible}
//       onHide={onHide}
//       style={{ width: '90vw', maxWidth: '800px' }}
//       dismissableMask
//       draggable={false}
//       pt={{
//         root: {
//           style: {
//             borderRadius: '20px',
//             overflow: 'hidden',
//             boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
//           },
//         },
//       }}
//     >
//       <div>
//         {/* Header */}
//         <div style={{
//           background: 'linear-gradient(135deg, #0f3d3e 0%, #1a5555 50%, #0a2f2f 100%)',
//           borderRadius: '16px',
//           padding: '24px',
//           marginBottom: '24px',
//           color: 'white',
//           position: 'relative',
//           overflow: 'hidden'
//         }}>
//           <div style={{
//             position: 'absolute',
//             top: '-50px',
//             right: '-50px',
//             width: '150px',
//             height: '150px',
//             background: 'rgba(212, 241, 126, 0.1)',
//             borderRadius: '50%',
//             filter: 'blur(40px)'
//           }} />
//           <div style={{ position: 'relative', zIndex: 1 }}>
//             <div style={{
//               display: 'inline-block',
//               padding: '4px 10px',
//               background: 'rgba(212, 241, 126, 0.2)',
//               borderRadius: '16px',
//               marginBottom: '8px',
//               fontSize: '0.65rem',
//               fontWeight: '600',
//               letterSpacing: '0.5px',
//               textTransform: 'uppercase'
//             }}>
//               Study Material {material.is_course_module_active && (
//                 <span style={{ marginLeft: '6px', color: '#d4f17e' }}>• Course Module</span>
//               )}
//             </div>
//             <h2 style={{ margin: '0 0 6px 0', fontSize: '1.4rem', fontWeight: '700' }}>
//               {material.title}
//             </h2>
//             <p style={{ margin: '0', color: 'rgba(255,255,255,0.7)', fontSize: '0.8rem' }}>
//               <i className="pi pi-calendar" style={{ marginRight: '6px' }}></i>
//               {formatDate(material.created_at)}
//             </p>
//           </div>
//         </div>

//         {/* Content */}
//         <div style={{
//           background: 'white',
//           borderRadius: '12px',
//           padding: '20px',
//           marginBottom: '20px'
//         }}>
//           <div style={{
//             fontSize: '0.95rem',
//             color: '#333',
//             lineHeight: '1.6',
//             whiteSpace: 'pre-wrap'
//           }}>
//             {material.body}
//           </div>
//         </div>

//         {/* Files */}
//         {material.files.length > 0 && (
//           <div style={{
//             background: '#f8f9fa',
//             borderRadius: '12px',
//             padding: '20px',
//             marginBottom: '20px',
//             border: '1px solid #e9ecef'
//           }}>
//             <h3 style={{
//               margin: '0 0 16px 0',
//               fontSize: '0.9rem',
//               fontWeight: '600',
//               color: '#0f3d3e'
//             }}>
//               <i className="pi pi-paperclip" style={{ marginRight: '8px' }}></i>
//               Attachments ({material.files.length})
//             </h3>
//             <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
//               {material.files.map((file, index) => (
//                 <div
//                   key={index}
//                   style={{
//                     display: 'flex',
//                     alignItems: 'center',
//                     justifyContent: 'space-between',
//                     padding: '12px',
//                     background: 'white',
//                     border: '1px solid #e0e0e0',
//                     borderRadius: '8px'
//                   }}
//                 >
//                   <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
//                     <i className={getFileIcon(file.mime_type)} style={{ fontSize: '1.4rem', color: '#0f3d3e' }} />
//                     <div style={{ flex: 1, minWidth: 0 }}>
//                       <div style={{
//                         fontSize: '0.85rem',
//                         fontWeight: '500',
//                         whiteSpace: 'nowrap',
//                         overflow: 'hidden',
//                         textOverflow: 'ellipsis'
//                       }}>
//                         {file.file_name}
//                       </div>
//                       <div style={{ fontSize: '0.75rem', color: '#666' }}>
//                         {formatFileSize(file.size)}
//                       </div>
//                     </div>
//                   </div>
//                   <a
//                     href={file.url}
//                     target="_blank"
//                     rel="noopener noreferrer"
//                     style={{
//                       padding: '6px 12px',
//                       background: '#0f3d3e',
//                       color: 'white',
//                       textDecoration: 'none',
//                       borderRadius: '6px',
//                       fontSize: '0.8rem',
//                       fontWeight: '600'
//                     }}
//                   >
//                     <i className="pi pi-download" style={{ marginRight: '4px' }}></i>
//                     Download
//                   </a>
//                 </div>
//               ))}
//             </div>
//           </div>
//         )}

//         {/* Video Links */}
//         {material.video_links.length > 0 && (
//           <div style={{
//             background: '#f8f9fa',
//             borderRadius: '12px',
//             padding: '20px',
//             marginBottom: '20px',
//             border: '1px solid #e9ecef'
//           }}>
//             <h3 style={{
//               margin: '0 0 16px 0',
//               fontSize: '0.9rem',
//               fontWeight: '600',
//               color: '#0f3d3e'
//             }}>
//               <i className="pi pi-video" style={{ marginRight: '8px' }}></i>
//               Video Links ({material.video_links.length})
//             </h3>
//             <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
//               {material.video_links.map((link, index) => (
//                 <a
//                   key={index}
//                   href={link}
//                   target="_blank"
//                   rel="noopener noreferrer"
//                   style={{
//                     padding: '12px',
//                     background: 'white',
//                     border: '1px solid #e0e0e0',
//                     borderRadius: '8px',
//                     color: '#0f3d3e',
//                     textDecoration: 'none',
//                     fontSize: '0.85rem',
//                     display: 'flex',
//                     alignItems: 'center',
//                     gap: '8px',
//                     transition: 'all 0.2s ease'
//                   }}
//                   onMouseEnter={(e) => {
//                     e.currentTarget.style.background = '#f5f5f5';
//                     e.currentTarget.style.borderColor = '#0f3d3e';
//                   }}
//                   onMouseLeave={(e) => {
//                     e.currentTarget.style.background = 'white';
//                     e.currentTarget.style.borderColor = '#e0e0e0';
//                   }}
//                 >
//                   <i className="pi pi-external-link"></i>
//                   <span style={{
//                     flex: 1,
//                     whiteSpace: 'nowrap',
//                     overflow: 'hidden',
//                     textOverflow: 'ellipsis'
//                   }}>
//                     {link}
//                   </span>
//                 </a>
//               ))}
//             </div>
//           </div>
//         )}

//         {/* Actions */}
//         <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
//           <GlobalButton
//             label="Close"
//             variant="outline"
//             onClick={onHide}
//           />
//         </div>
//       </div>
//     </Dialog>
//   );
// };

// export default ViewStudyMaterialModal;